import React, { useEffect, useRef } from "react";
import { Stepper, Step, StepLabel, Typography } from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BaseButton from "../../../components/ui/BaseButton";
import routes from "../../../routes";
import { styled } from "@mui/material/styles";
import StepConnector, { stepConnectorClasses } from "@mui/material/StepConnector";
import { ReactComponent as Check } from "../../../assets/global-icons/checked.svg";
import { useQuery } from "@tanstack/react-query";
import { fetchJobDetail } from "../../../services/api/workOrder";
import { updateWorkOrderDetail } from "../../../store/workOrder/workOrderSlice";
import { showToast } from "../../../store/toast/toastSlice";
import { useDispatch } from "react-redux";
import useAuth from "../../../hooks/useAuth";
import { useHasAcceptedProposal } from "../../../hooks/useHasAcceptedProposal";
import { UserType } from "../../../utils/enum";
import {
  buildValidationMessage,
  focusFirstInvalidField,
  normalizeValidationResult,
} from "../../../utils/formErrors";
import useScrollToTop from "../../../hooks/useScrollToTop";

const QontoConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 8,
    left: "calc(-50% + 16px)",
    right: "calc(50% + 16px)",
  },
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: theme.palette.primary.main,
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.mode === "dark" ? theme.palette.grey[800] : "#DCDCDC",
    borderTopWidth: 7,
    borderRadius: 1,
  },
}));

const QontoStepIconRoot = styled("div")(({ theme, ownerState }) => ({
  color: theme.palette.mode === "dark" ? theme.palette.grey[700] : "#DCDCDC",
  display: "flex",
  height: 22,
  zIndex: 1,
  alignItems: "center",
  ...(ownerState.active && {
    color: theme.palette.primary.main,
  }),
  "& .QontoStepIcon-completedIcon": {
    width: 34,
    height: 34,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
  },
  "& .QontoStepIcon-circle": {
    width: 34,
    height: 34,
    borderRadius: "50%",
    backgroundColor: "#DCDCDC",
  },
  "& .QontoStepIcon-activeIcon": {
    width: 34,
    height: 34,
    borderRadius: "50%",
    backgroundColor: theme.palette.primary.main,
  },
}));

function QontoStepIcon(props) {
  const { active, completed, className } = props;
  return (
    <QontoStepIconRoot ownerState={{ active }} className={className}>
      {completed ? (
        <div className="flex items-center justify-center QontoStepIcon-completedIcon">
          <Check />
        </div>
      ) : active ? (
        <div className="QontoStepIcon-activeIcon" />
      ) : (
        <div className="QontoStepIcon-circle" />
      )}
    </QontoStepIconRoot>
  );
}

const CLIENT_STEPS = ["summary", "scheduling", "payment", "assessment"];
// Customers raise a ticket without the internal Assessment step.
const CUSTOMER_STEPS = ["summary", "scheduling", "payment"];
// Once the customer has accepted a client's proposal only the purchase order is
// theirs to add, so the edit wizard is just the payment step (with the agreed
// rates and expenses locked there). The API drops everything else anyway.
const CUSTOMER_PO_ONLY_STEPS = ["payment"];

const WorkOrderStepper = ({ isBannerVisible }) => {
  const { authRole } = useAuth();
  const isCustomer = authRole === UserType.CUSTOMER;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();

  const hasAcceptedProposal = useHasAcceptedProposal(params.id, isCustomer && !!params.id);
  const steps = isCustomer
    ? hasAcceptedProposal
      ? CUSTOMER_PO_ONLY_STEPS
      : CUSTOMER_STEPS
    : CLIENT_STEPS;

  const stepRefs = useRef(CLIENT_STEPS.map(() => React.createRef()));

  const currentStepRoute = location.pathname.split("/")[3]?.toLowerCase();
  const currentStepIndex = steps.indexOf(currentStepRoute);

  // Every "Edit" link opens the summary step; a customer who has accepted a
  // proposal is sent straight to the only step left to them.
  useEffect(() => {
    if (hasAcceptedProposal && params.id && !steps.includes(currentStepRoute)) {
      navigate(`/workOrder/edit/${steps[0]}/${params.id}`, { replace: true });
    }
  }, [hasAcceptedProposal, params.id, currentStepRoute, steps, navigate]);

  const createWorkOrderRoute = routes.find((route) => route.path === "workOrder/create");
  const allSteps = createWorkOrderRoute?.childs
    .filter((step) => steps.includes(step.path?.toLowerCase()))
    .map((step) => ({
      ...step,
      ref: React.createRef(),
    }));

  // Fetch Work Order Data for Edit using React Query
  const {
    isLoading: isWorkOrderDetailsLoading,
    error: isWorkOrderDetailsError,
    data: workOrderDetails,
  } = useQuery({
    queryKey: ["workOrderDetails"],
    queryFn: () => fetchJobDetail(params.id),
    enabled: !!params.id,
  });

  const isEditMode = !!params?.id && !!workOrderDetails?.data;

  useEffect(() => {
    if (isEditMode) {
      const formattedWorkOrder = formatWorkOrderDetails(workOrderDetails?.data);
      dispatch(updateWorkOrderDetail(formattedWorkOrder));
    } else {
      // Creating a fresh work order: clear the "raised by" marker so it can't
      // linger from a customer ticket viewed earlier and lock the client field,
      // and drop any documents hydrated from a work order edited beforehand —
      // updateWorkOrderDetail merges, so they would otherwise carry over.
      dispatch(
        updateWorkOrderDetail({
          summary: {
            createdByRole: null,
            customerFiles: { isCustomerFilesVisible: false, files: [] },
            customerFilesBaseline: [],
          },
        }),
      );
      // dispatch(resetWorkOrderForm());
    }
  }, [workOrderDetails]);

  const formatWorkOrderDetails = (workOrder) => {
    const formattedQualificationSubTypes = workOrder?.qualificationSubTypes?.reduce((acc, curr) => {
      const qualificationTypeId = curr.qualificationType.id;

      // Check if the qualificationTypeId already exists in the accumulator
      const existingQualification = acc.find(
        (item) => item.qualificationTypeId === qualificationTypeId,
      );

      if (existingQualification) {
        // If it exists, push the id into qualificationSubTypes
        existingQualification.qualificationSubTypes.push(curr.id);
      } else {
        // If not, create a new entry with the qualificationTypeId and the first qualificationSubType id
        acc.push({
          id: Date.now(),
          qualificationTypeId: qualificationTypeId,
          qualificationSubTypes: [curr.id],
        });
      }

      return acc;
    }, []);

    return {
      // Surfaced so the header can offer a validated "Review & Publish" when a
      // client is editing a customer-raised PENDING_APPROVAL ticket.
      status: workOrder?.status,
      summary: {
        // Who raised the ticket — a client reviewing a customer's may not change
        // the client it was raised against. Only a customer submission reaches
        // PENDING_APPROVAL, so that stands in until the API returns the role.
        // Derived per work order here (never from leftover form state) so a
        // client-created one always resets this to null.
        createdByRole:
          workOrder?.createdBy?.role?.role ??
          (workOrder?.status === "PENDING_APPROVAL" ? UserType.CUSTOMER : null),
        overview: {
          title: workOrder?.title,
          // The customer picks the SLA when raising the ticket; carry it across so
          // the client sees their choice on the review screen.
          serviceLevelAgreeement: workOrder?.serviceLevelAgreeement ?? null,
          isExportDisable: workOrder?.isExportDisable,
          clientId: workOrder?.client?.id,
          projectId: workOrder?.project?.id,
          isExportDisable: workOrder?.isExportDisable,
          isCounterOffersDisable: workOrder?.isCounterOffersDisable,
          isGpsRequire: workOrder?.isGpsRequire,
          incidentId: workOrder?.incidentId,
          // Both team pickers are read-only and driven off these ids; without them
          // the fields come up empty when a client opens a ticket to review it.
          teamId: workOrder?.team?.id ?? null,
          teamName: workOrder?.team?.name ?? null,
          secondaryTeamId: workOrder?.secondaryTeam?.id ?? null,
          secondaryTeamName: workOrder?.secondaryTeam?.name ?? null,
        },
        serviceDescription: {
          description: workOrder?.description,
          confidentialInformation: workOrder?.confidentialInformation,
        },
        workExperience: {
          categoryId: workOrder?.category?.id,
          vendorId: workOrder?.category?.vendor?.id,
          workOrderServices: workOrder?.category?.vendor?.services.map((service) => service?.id),
        },
        // Same shape DocumentsForm builds for the details-page modal, so the card
        // renders straight from the store without needing props threaded through.
        customerFiles: {
          isCustomerFilesVisible: !!workOrder?.customerFiles?.length,
          files: (workOrder?.customerFiles || []).map((file) => ({
            id: file.id,
            name: file.filename,
            url: file.url,
            isRemote: true,
          })),
        },
        customerFilesBaseline: (workOrder?.customerFiles || []).map((file) => file.id),
      },

      scheduling: {
        qualifications: formattedQualificationSubTypes,
        location: {
          addressId: workOrder?.address?.id,
          type: "EXACT_LOCATION",
          displayName: workOrder?.address?.displayName,
          locationType: workOrder?.address?.locationType,
          country: workOrder?.address?.country,
          addressLine1: workOrder?.address?.addressLine1,
          addressLine2: workOrder?.address?.addressLine2,
          city: workOrder?.address?.city,
          state: workOrder?.address?.state,
          zipCode: workOrder?.address?.zipCode,
          longitude: workOrder?.address?.longitude,
          latitude: workOrder?.address?.latitude,
          saveForFuture: workOrder?.saveForFuture,
          isRemote: workOrder?.isRemote,
          timeZone: workOrder?.timeZone,
        },
        schedule: {
          // A draft may have been saved before a schedule type was chosen, and this
          // hydration replaces the whole `schedule` object — so without a fallback
          // the radio would show HARD_START selected (its defaultValue) while the
          // date and time fields, keyed off this value, rendered nothing.
          scheduleType: workOrder?.scheduleType || "HARD_START",
          orderStartDate: workOrder?.orderStartDate,
          orderStartTime: workOrder?.orderStartTime,
          orderEndDate: workOrder?.orderEndDate,
          orderEndTime: workOrder?.orderEndTime,
          timeZone: workOrder?.timeZone,
        },
      },
      payment: {
        pay: {
          paymentTerms: workOrder?.paymentTerms,
          paymentType: workOrder?.paymentType,
          hourlyRate: workOrder?.hourlyRate,
          minimumHours: workOrder?.minimumHours,
          fixedAmount: workOrder?.fixedAmount,
          halfDayRate: workOrder?.halfDayRate,
          fullDayRate: workOrder?.fullDayRate,
          fthRate: workOrder?.fthRate,
          additionalHourlyRate: workOrder?.additionalHourlyRate,
          nonBusinessHourRate: workOrder?.nonBusinessHourRate,
          customHours: workOrder?.customHours,
          customHoursRate: workOrder?.customHoursRate,
          currency: workOrder?.currency,
          purchaseOrderNumber: workOrder?.purchaseOrderNumber,
          purchaseOrders: workOrder?.purchaseOrders ?? [],

          // Engineer pay was saved but never read back, so reopening a work
          // order showed an empty section — and saving again wrote those blanks
          // over what was stored. formatWorkOrderPayload gates every engineer
          // field on enableCustomRates, which is UI-only and not persisted, so
          // derive it from whether any engineer rate actually exists.
          enableCustomRates: [
            workOrder?.engineerCurrency,
            workOrder?.engineerHourlyRate,
            workOrder?.engineerHalfDayRate,
            workOrder?.engineerFullDayRate,
            workOrder?.engineerFixedAmount,
            workOrder?.engineerFthRate,
          ].some((value) => value !== null && value !== undefined && value !== ""),
          enableClientAdditionalHourRate: !!workOrder?.additionalHourlyRate,
          enableNonBusinessHourRate: !!workOrder?.nonBusinessHourRate,
          enableEngineerAdditionalHourRate: !!workOrder?.engineerAdditionalHourlyRate,
          engineerPaymentType: workOrder?.engineerPaymentType,
          engineerCurrency: workOrder?.engineerCurrency,
          engineerHourlyRate: workOrder?.engineerHourlyRate,
          engineerHalfDayRate: workOrder?.engineerHalfDayRate,
          engineerFullDayRate: workOrder?.engineerFullDayRate,
          engineerFixedAmount: workOrder?.engineerFixedAmount,
          engineerFthRate: workOrder?.engineerFthRate,
          engineerMinimumHours: workOrder?.engineerMinimumHours,
          engineerAdditionalHourlyRate: workOrder?.engineerAdditionalHourlyRate,
        },

        expenses: workOrder?.Expense ?? [],
      },
    };
  };

  // Keyed on the step route, so it covers Next, Back, browser back/forward and
  // direct URLs alike — rather than only the two button handlers.
  useScrollToTop(currentStepRoute);

  const handleNext = () => {
    // Validate before moving the viewport — the old order scrolled the user to
    // the top of the page just as the errors appeared further down.
    const { valid, errors } = normalizeValidationResult(
      stepRefs.current[currentStepIndex]?.current?.isValid?.(),
    );

    if (!valid) {
      const message = buildValidationMessage(errors);
      if (message) {
        dispatch(showToast({ message, type: "error" }));
      }
      focusFirstInvalidField();
      return;
    }

    const nextStepIndex = currentStepIndex + 1;

    if (nextStepIndex < steps.length) {
      const path = params.id
        ? `/workOrder/edit/${steps[nextStepIndex]}/${params.id}`
        : `/workOrder/create/${steps[nextStepIndex]}`;
      navigate(path);
    }
  };

  const handleBack = () => {
    const prevStepIndex = currentStepIndex - 1;
    if (prevStepIndex >= 0) {
      const path = params.id
        ? `/workOrder/edit/${steps[prevStepIndex]}/${params.id}`
        : `/workOrder/create/${steps[prevStepIndex]}`;
      navigate(path);
    }
  };

  const handleStep = (stepIndex) => () => {
    /*  const path = params.id
      ? `/workOrder/edit/${steps[stepIndex]}/${params.id}`
      : `/workOrder/create/${steps[stepIndex]}`;
    navigate(path);
    window.scrollTo({ top: 0, behavior: "smooth" }); */
  };

  return (
    <div>
      <Stepper
        className={`sticky bg-[#F6F6F6] z-10 pt-6 pb-2 ${
          isBannerVisible ? "sm:top-[162px] top-[196px]" : "sm:top-[110px] top-[145px]"
        }`}
        activeStep={currentStepIndex}
        alternativeLabel
        connector={<QontoConnector />}
      >
        {steps.map((label, index) => (
          <Step key={label} onClick={handleStep(index)}>
            <StepLabel StepIconComponent={QontoStepIcon} className="cursor-pointer">
              <p
                className={`font-medium capitalize ${currentStepIndex === index && "text-primary"}`}
              >
                {/* The Assessment step's forms are parked until they ship. */}
                {label}{" "}
                {index === 3 ? <Typography variant="caption">(Coming Soon)</Typography> : ""}
              </p>
            </StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* overflowAnchor none: the step's cards resize as their data arrives, and
          scroll anchoring would shift the viewport to compensate — which is what
          dragged the page back down right after landing on a new step. */}
      <div
        style={{ overflowAnchor: "none" }}
        className={`px-[2px] pb-5 ${isBannerVisible ? "mt-28" : "mt-16"}`}
      >
        {allSteps.map((step, index) => (
          <div key={step.path}>
            {location.pathname.includes(step.path) &&
              React.cloneElement(step.component, {
                ref: stepRefs.current[index],
                workOrderDetails: workOrderDetails?.data,
                isEditMode,
              })}
          </div>
        ))}

        <div
          className={`flex ${
            steps[currentStepIndex] === "summary" ? "justify-end" : "justify-between"
          }  pt-5`}
        >
          {currentStepIndex !== 0 && (
            <BaseButton
              onClick={handleBack}
              className="w-28 bg-[#EDEDED] pt-2 pb-2 pl-7 pr-7 rounded-bl-lg  rounded-tl-lg rounded-tr-lg rounded-br-none"
            >
              <p>Back</p>
            </BaseButton>
          )}

          {currentStepIndex < steps.length - 1 && (
            <BaseButton
              onClick={handleNext}
              className="w-28 bg-primary  pt-2 pb-2 pl-7 pr-7 rounded-bl-lg  rounded-tl-lg rounded-tr-lg rounded-br-none"
            >
              Next
            </BaseButton>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkOrderStepper;
